import React from 'react';

const ProfileEditView = ({
  displayName,
  setDisplayName,
  phoneNumber,
  setPhoneNumber,
  isPhoneVerified,
  setIsPhoneVerified,
  showPhoneVerification,
  verificationCode,
  setVerificationCode,
  loading,
  error,
  successMessage,
  phoneError,
  verificationError,
  isSendingCode,
  isVerifying,
  handleSendVerificationCode,
  handleVerifyCode,
  handleSubmit,
  resetVerification,
  onClose
}) => {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-2xl leading-6 font-bold text-white mb-6" id="modal-title">
                  Edit Profile
                </h3>
                
                {error && (
                  <div className="mb-4 p-3 bg-red-900 bg-opacity-50 rounded-md">
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                )}
                
                {successMessage && (
                  <div className="mb-4 p-3 bg-green-900 bg-opacity-50 rounded-md">
                    <p className="text-green-300 text-sm">{successMessage}</p>
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-1">
                      Full Name
                    </label>
                    <div className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white">
                      {displayName || 'Not set'}
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-300 mb-1">
                      Phone Number
                    </label>
                    <div className="flex items-center gap-2 mb-1">
                      <input
                        type="tel"
                        id="phoneNumber"
                        className={`flex-1 px-3 py-2 bg-gray-800 border ${phoneError ? 'border-red-500' : 'border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="+972xxxxxxxxx"
                        disabled={isPhoneVerified && !showPhoneVerification}
                      />
                      {isPhoneVerified ? (
                        <div className="flex items-center">
                          <div className="px-3 py-1 bg-green-900 text-green-300 rounded-full text-xs flex items-center ml-2">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            Verified
                          </div>
                          <button
                            type="button"
                            className="text-blue-400 hover:text-blue-300 transition text-sm"
                            onClick={() => {
                              setIsPhoneVerified(false);
                              resetVerification();
                            }}
                          >
                            Change
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={handleSendVerificationCode}
                          disabled={isSendingCode || !phoneNumber}
                        >
                          {isSendingCode ? (
                            <span className="flex items-center">
                              <span className="mr-2 h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                              <span>Sending...</span>
                            </span>
                          ) : (
                            "Send Code"
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* reCAPTCHA container with improved visibility and error handling */}
                  <div className="my-6 border border-gray-700 rounded-md overflow-hidden">
                    <div 
                      id="recaptcha-container" 
                      className="w-full min-h-[100px] flex items-center justify-center p-2" 
                      role="presentation"
                      tabIndex="0"
                    ></div>
                  </div>
                  
                  {phoneError && (
                    <div className="text-red-400 text-sm flex items-start gap-2 -mt-2 mb-4">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>{phoneError}</span>
                    </div>
                  )}
                  
                  {showPhoneVerification && (
                    <div className="mt-4">
                      <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-300 mb-1">
                        Verification Code
                      </label>
                      <div className="flex flex-col space-y-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            id="verificationCode"
                            className={`flex-1 px-3 py-2 bg-gray-800 border ${verificationError ? 'border-red-500' : 'border-gray-700'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center`}
                            value={verificationCode}
                            onChange={(e) => setVerificationCode(e.target.value)}
                            maxLength="6"
                            placeholder="xxxxxx"
                          />
                          <button
                            type="button"
                            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleVerifyCode}
                            disabled={isVerifying || !verificationCode || verificationCode.length < 4}
                          >
                            {isVerifying ? (
                              <span className="flex items-center">
                                <span className="mr-2 h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                <span>Verifying...</span>
                              </span>
                            ) : (
                              "Verify Code"
                            )}
                          </button>
                        </div>
                        <div className="flex justify-between items-center">
                          {verificationError && (
                            <div className="text-red-400 text-sm flex items-start gap-2">
                              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>{verificationError}</span>
                            </div>
                          )}
                          <button
                            type="button"
                            className="text-blue-400 hover:text-blue-300 transition text-sm ml-auto"
                            onClick={() => {
                              resetVerification();
                              handleSendVerificationCode();
                            }}
                          >
                            Send New Code
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="pt-4 flex justify-end gap-3 border-t border-gray-800 mt-6">
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-md text-sm"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center">
                          <span className="mr-2 h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                          <span>Saving...</span>
                        </span>
                      ) : (
                        "Save Changes"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditView; 